import type { FC } from "react"
import React, { useRef } from "react"

import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import styled from "styled-components"

import COLORS from "./COLORS"
import { findProjectedNodeState } from "../../state/elements"
import {
  nodeSelectionPositionState,
  nodeSelectionState,
} from "../../state/elements/nodeSelection"
import { useTryToDrawEdge } from "../../state/elements/transactions"
import { useEraseNode } from "../../state/elements/transactions/eraseNode"
import { flawedNodeIndex, focusedFlawIndexSoft } from "../../state/flaws"
import { shiftIsDownState } from "../../state/inputs"
import { toolState } from "../../state/tool"
import type { MouseControlsPerTool } from "../../state/tool"
import { Drag } from "../Interactions"

const IS_DEBUG = true

const NodeCircle = styled.circle`
  &:hover {
    /* stroke-width: 8px; */
    r: 6px;
  }
`

const Node_Internal: FC<{ id: string }> = ({ id }) => {
  const node = useRecoilValue(findProjectedNodeState(id))
  const deleteNode = useEraseNode(id)
  const [nodeSelection, setNodeSelection] = useRecoilState(nodeSelectionState)
  const flawedIds = useRecoilValue(flawedNodeIndex)
  const focusedFlawIds = useRecoilValue(focusedFlawIndexSoft)
  const tryToDrawEdge = useTryToDrawEdge(id)
  const nodeRef = useRef(null)
  const shiftIsDown = useRecoilValue(shiftIsDownState)
  const tool = useRecoilValue(toolState)

  const displaceSelection = useSetRecoilState(nodeSelectionPositionState)

  const isInvalid = flawedIds.includes(id)
  const isShownInvalid = isInvalid && focusedFlawIds.includes(id)
  const coreColor = isInvalid ? COLORS.INVALID : COLORS.VALID
  const isSelected = nodeSelection.has(id)

  const mouseDownFor: MouseControlsPerTool = {
    Select: (e) => {
      e.stopPropagation()
      if (shiftIsDown) {
        setNodeSelection((current) => {
          if (current.has(id)) {
            current.delete(id)

            return current
          }

          return current.add(id)
        })
      } else if (!nodeSelection.has(id)) {
        setNodeSelection(new Set([id]))
      }
    },
    Draw: (e) => (
      e.stopPropagation(),
      tryToDrawEdge(),
      setNodeSelection(new Set([id]))
    ),
    Erase: (e) => (e.stopPropagation(), deleteNode()),
  }

  return (
    <>
      <Drag
        tool={`Select`}
        position={node}
        onDrag={displaceSelection}
        nodeRef={nodeRef}
        onMouseDown={mouseDownFor[tool]}
      >
        <NodeCircle
          ref={nodeRef}
          cx={node.x}
          cy={node.y}
          r={isSelected ? 5 : 4}
          stroke={coreColor}
          fill={isSelected ? coreColor : `#fff`}
          strokeWidth={1.5}
        />
      </Drag>
      {/* {isShownInvalid && <CoordinateTooltip says={id} where={node} />} */}
    </>
  )
}

export const Node = React.memo(Node_Internal)
